import React, { ReactElement } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useInlineCard,
  useNonIgnoredAlerts,
  useRenderableAlerts,
} from "../../store/hooks";
import { recalculateRects, getFeedbackType } from "./writingAlertsSlice";
import Overlay, { UNDERLINE_OVERLAY_COLOR } from "../Overlay";
import { HoverLogic } from "./hooks/useUnderlineCardHover";
import { UnderlineCalculations } from "./hooks/useUnderlineCalculations";
import { Attachment } from "../../injection/Attachment";
import { attachAlert } from "./inlineCardSlice";
import { editorAdapter } from "../../editorsSlice";
// import { useDeveloperMode, useShowOverlays } from "../../storage/hooks";
// import ShadowPortal from "../../ShadowPortal";
import Underline from "./Underline";

interface Props {
  editorId: number;
  underlineElement: HTMLElement;
  attachment: Attachment;
  hoverLogic: HoverLogic;
}

function UnderlineShadowContainer({
  editorId,
  attachment,
  underlineElement,
  hoverLogic,
}: Props): ReactElement {
  const dispatch = useAppDispatch();
  const editor = useAppSelector((state) =>
    editorAdapter.getSelectors().selectById(state.editors, editorId)
  );
  const writingAlerts = useNonIgnoredAlerts(editorId, attachment.editor);
  const renderableWritingAlerts = useRenderableAlerts(
    editorId,
    attachment.editor
  );
  // const [devMode] = useDeveloperMode();
  // const [showOverlaysDebug] = useShowOverlays();
  const inlineCard = useInlineCard();

  if (!editor) {
    return <></>;
  }

  return (
    // <ShadowPortal container={underlineElement}>
    <>
      <UnderlineCalculations
        editorNode={attachment.editor}
        alerts={writingAlerts}
        // TODO: type safety explanation
        editor={editor!}
        onCalculation={(calculations) => {
          dispatch(
            recalculateRects(
              calculations
                .filter((u) => u.rects.length > 0)
                .map((u) => {
                  const { alert, rects } = u;
                  return {
                    id: alert.id,
                    overlayPositions: rects,
                  };
                })
            )
          );
        }}
        deps={[dispatch, recalculateRects, editor]}
      />
      <div style={{ position: "absolute", top: 0, left: 0 }}>
        <Overlay
          editorId={editorId}
          debug={
            // (devMode && showOverlaysDebug && UNDERLINE_OVERLAY_COLOR) || ""
            ""
          }
          relativeElement={underlineElement}
        >
          {renderableWritingAlerts.map((writingAlert) => {
            const { ui } = writingAlert;

            if (ui.overlayPositions.length === 0) {
              return <></>;
            }
            return ui.overlayPositions.map((position, i) => {
              const { top, left, height: uHeight, width: uWidth } = position;

              // TODO(tidy): where to keep this constant
              const TOP_PADDING = 2;

              return (
                <Underline
                  type={getFeedbackType(writingAlert)}
                  // The position within the array has semantic meaning here
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${writingAlert.id}+${i}`}
                  height={uHeight + TOP_PADDING}
                  width={uWidth}
                  top={top}
                  left={left}
                  observedElement={attachment.editor}
                  observedLeft={
                    editor.boundingClientRect.left +
                    editor.border.left -
                    editor.scrollLeft
                  }
                  observedTop={
                    editor.boundingClientRect.top +
                    editor.border.top -
                    editor.scrollTop
                  }
                  isHighlighted={
                    !!inlineCard.alert &&
                    inlineCard.alert.id === writingAlert.id &&
                    inlineCard.isHovering
                  }
                  onHoverStarted={() => {
                    dispatch(
                      attachAlert({
                        alert: {
                          id: writingAlert.id,
                          positionIndex: i,
                        },
                      })
                    );
                    hoverLogic.markIsHovering(
                      hoverLogic.alertSource(writingAlert)
                    );
                  }}
                  onHoverEnded={() => {
                    hoverLogic.markIsNotHovering(
                      hoverLogic.alertSource(writingAlert)
                    );
                  }}
                  gated={writingAlert.feedback.gated}
                />
              );
            });
          })}
        </Overlay>
      </div>
      {/* </ShadowPortal> */}
    </>
  );
}

export default UnderlineShadowContainer;
